import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TableConfigDialog } from "../tables/TableConfigDialog";
import { contactFiltersAreEqual, type ContactsFilters } from "./contactFilters";
import { ContactsFilterConfigForm } from "./ContactsFilterConfigForm";
import { ColumnConfigForm, type ColumnConfigFormProps } from "../tables/ColumnConfigForm";
import type { UseTableViewConfigResult } from "../../hooks/useTableViewConfig";

export interface ContactsTableConfigDialogProps
  extends Pick<UseTableViewConfigResult<ContactsFilters>, "onApplyConfig" | "activeFilters"> {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClearAll?: () => void;
  title?: string;
  description?: string;
  columnConfigFormProps: Pick<
    ColumnConfigFormProps,
    "columnVisibility" | "orderedVisibleColumnIds" | "combinedFieldToColumnConfigMap" | "title"
  >;
  isDirty?: boolean;
}

export const ContactsTableConfigDialog = ({
  onApplyConfig,
  activeFilters,
  onClearAll,
  title,
  description,
  columnConfigFormProps: {
    columnVisibility,
    orderedVisibleColumnIds,
    combinedFieldToColumnConfigMap,
    title: columnConfigTitle,
  },
  isDirty: providedIsDirty,
  ...rest
}: ContactsTableConfigDialogProps) => {
  const { t } = useTranslation("contacts");

  const [stagedColumnIds, setStagedColumnIds] = useState<string[]>(orderedVisibleColumnIds);
  const [stagedFilters, setStagedFilters] = useState<ContactsFilters>(activeFilters);

  const isColumnsDirty = useMemo(() => {
    if (stagedColumnIds.length !== orderedVisibleColumnIds.length) return true;
    return stagedColumnIds.some((id, i) => id !== orderedVisibleColumnIds[i]);
  }, [stagedColumnIds, orderedVisibleColumnIds]);

  const isFiltersDirty = useMemo(
    () => !contactFiltersAreEqual(stagedFilters, activeFilters),
    [stagedFilters, activeFilters]
  );

  const isDirty = providedIsDirty || isColumnsDirty || isFiltersDirty;

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setStagedColumnIds(orderedVisibleColumnIds);
      setStagedFilters(activeFilters);
    }
    rest.onOpenChange?.(open);
  };

  const handleApply = () => {
    onApplyConfig(stagedColumnIds, stagedFilters);
  };

  return (
    <TableConfigDialog
      {...rest}
      onOpenChange={handleOpenChange}
      onClearAll={onClearAll}
      title={title ?? t("Configure Contacts Table View")}
      description={description ?? t("Add and remove columns, and apply filters to narrow down the contacts")}
      isDirty={isDirty}
      onApply={handleApply}
    >
      <ColumnConfigForm
        columnVisibility={columnVisibility}
        orderedVisibleColumnIds={orderedVisibleColumnIds}
        combinedFieldToColumnConfigMap={combinedFieldToColumnConfigMap}
        stagedColumnIds={stagedColumnIds}
        onStagedColumnIdsChange={setStagedColumnIds}
        title={columnConfigTitle}
        isDirty={isColumnsDirty}
        reset={() => setStagedColumnIds(orderedVisibleColumnIds)}
      />
      <ContactsFilterConfigForm stagedFilters={stagedFilters} onStagedFiltersChange={setStagedFilters} />
    </TableConfigDialog>
  );
};
